import { Box, Button, Link, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import React, { useCallback, useState } from 'react';
import { useVerifyOTP } from './hooks/useVerifyOTP';
import { useResendOTP } from './hooks/useResendOTP';
import { useLogin, useLoginError } from './reducer/useLogin';
import { SecuritySkeleton } from '../common/SecuritySkeleton';
import { OtpField } from '../common/OtpField';
import { LoginErrorMessage } from './LoginErrorMessage';
import { contactLink } from '../common/ContactLink';
import { useResetLoginState } from './hooks/useResetLoginState';
import { LoadingIndicator } from '@xolv-mybrightlink/common';

export const LoginOtpInputForm = () => {
  const errorCode = useLoginError();
  const [otp, setOtp] = useState<string>();
  const [didResendOtp, setDidResendOtp] = useState(false);
  const loginState = useLogin();
  const verifyOtp = useVerifyOTP();
  const resendOtp = useResendOTP();
  const resetLoginState = useResetLoginState();

  const triggerVerifyOtp = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (otp) {
        await verifyOtp(otp);
      }
    },
    [otp, verifyOtp],
  );

  const triggerResendOtp = useCallback(async () => {
    setOtp(undefined);
    setDidResendOtp(true);
    await resendOtp();
  }, [resendOtp]);

  return (
    <SecuritySkeleton onBackPressed={resetLoginState}>
      <Box
        component='form'
        noValidate
        onSubmit={triggerVerifyOtp}
        sx={{
          mt: 15,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant='body1'
          sx={{
            color: '#626a7a',
            fontWeight: 500,
          }}
        >
          <FormattedMessage id='login.otp.button.signin.label' />
        </Typography>

        <Typography
          variant='body2'
          sx={{
            color: '#626a7a',
            mt: 5,
          }}
        >
          <FormattedMessage id='login.otp.button.signin.helper' />
        </Typography>
        <OtpField otp={otp ?? ''} setOtp={setOtp} dataTestid='otpInput' />
        <Button
          type='submit'
          fullWidth
          variant='contained'
          size='large'
          data-testid='submitOtpButton'
          sx={{
            mt: 6,
            mb: 4,
            borderRadius: 30,
            py: 3.5,
          }}
          disabled={!otp || otp.length != 6}
          disableElevation
        >
          <FormattedMessage id='login.otp.button.signin.label' />
        </Button>
      </Box>
      {errorCode && <LoginErrorMessage errorCode={errorCode} />}
      <Typography
        variant='body2'
        sx={{
          color: '#626a7a',
          mt: 4,
        }}
      >
        <FormattedMessage
          id='login.otp.button.resend.label'
          values={{
            resend: (text) => (
              <Link
                data-testid='resendOtpButton'
                component='button'
                variant='body2'
                onClick={triggerResendOtp}
                sx={{
                  fontWeight: 500,
                }}
              >
                {text}
              </Link>
            ),
          }}
        />
      </Typography>
      {didResendOtp && !errorCode && (
        <Box
          sx={{
            mt: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant='body1' sx={{ textAlign: 'center' }}>
            <FormattedMessage
              id='login.otp.button.resend.success.label'
              values={{ contactLink }}
            />
          </Typography>
        </Box>
      )}
      {loginState.loading && <LoadingIndicator fullHeight overlay />}
    </SecuritySkeleton>
  );
};
